import { createAsyncThunk } from '@reduxjs/toolkit';
import get_address_from_coords from "functions/server/get_address_from_coords.js";

/**
 * Fetches an address from coordinates using an async thunk action.
 * 
 * @async
 * @function fetchAddress
 * @param {Object} payload - The payload object containing location data
 * @param {string} payload.uuid - Required UUID identifier for the request
 * @param {number} payload.lat - Latitude coordinate
 * @param {number} payload.lng - Longitude coordinate
 * @param {number} payload.zoom - Zoom level for the map
 * @param {Object} thunkAPI - Redux Toolkit thunk API object
 * @param {Function} thunkAPI.rejectWithValue - Function to return a rejected action with a custom payload
 * @returns {Promise<Object>} Returns an object containing the address and uuid
 * @returns {Object} responseDict - The response dictionary
 * @returns {string} responseDict.address - The fetched address
 * @returns {string} responseDict.uuid - The UUID from the payload
 * @throws {Error} Throws an error if uuid is not provided in the payload
 * @rejects {string} Returns 'Error fetching address' if the API response contains an error message
 */
export const fetchAddress = createAsyncThunk('posts/getAddress', async (payload, { rejectWithValue }) => {

    // Validate payload exists
    if (!payload || typeof payload !== 'object') {
        return rejectWithValue('Invalid payload: expected an object');
    }

    // Validate uuid
    if (!payload.uuid || typeof payload.uuid !== 'string') {
        return rejectWithValue('Invalid or missing uuid: expected a non-empty string');
    }

    // Validate latitude
    if (typeof payload.lat !== 'number' || payload.lat < -90 || payload.lat > 90) {
        return rejectWithValue('Invalid latitude: must be a number between -90 and 90');
    }

    // Validate longitude
    if (typeof payload.lng !== 'number' || payload.lng < -180 || payload.lng > 180) {
        return rejectWithValue('Invalid longitude: must be a number between -180 and 180');
    }

    try {
        const postJson = { lat: payload.lat, lng: payload.lng, zoom: payload.zoom + 4 };
        const response = await get_address_from_coords(postJson);
        
        if (response?.message?.includes('error processing')) {
            return rejectWithValue('Error fetching address');
        }

        return { address: response?.address, uuid: payload.uuid };
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch address');
    }

});
